/**
 * TurnFeedback — thumbs up/down rating for a single completed agent turn.
 *
 * See SodaBrainProtocol's turn-feedback spec (§7) for the full design. Key
 * points this component implements:
 *
 * - Renders nothing if `turnId` is undefined. This is the mount guard for
 *   every turn that isn't eligible for feedback: failed/cancelled turns,
 *   all `turnNumber: 0` sentinel response paths, the turn-save-failure
 *   fallback, and any turn persisted before this feature shipped. None of
 *   those have a `turnId`; none get a feedback UI.
 * - `feedback` is a `MessageFeedback` discriminated union (decision 12),
 *   read from the parent `Message`. This component never owns the
 *   authoritative transition itself — it only sends frames and sets the
 *   optimistic `pending` variant via `setMessages` (a
 *   `setMessages`-for-optimistic-local-state pattern).
 *   `useConversationSessions.ts`'s `handleFrame` is what actually resolves
 *   every `feedbackAck`, including sending a switch's second frame — see
 *   that file for why (a conversation-switch unmount must not orphan an
 *   in-flight continuation).
 * - Decision 10: a locked rating's buttons stay clickable, not disabled.
 *   Clicking the SAME button again deselects; clicking the OPPOSITE button
 *   switches. Decision 14: a down-switch collects its comment BEFORE
 *   sending anything — the textarea opens while `feedback` is still
 *   `{ rating: prior }` (not yet pending), and only Submit/Skip sends the
 *   `deselect` frame with the comment already attached to the `switch`
 *   state. This keeps every `pending` variant meaning exactly one thing
 *   ("a frame is in flight, awaiting its ack") with no exception for
 *   "waiting on user input" — a hook can't render a textarea, so the
 *   comment can never be collected mid-flight.
 * - A duplicate-race resolution (the acked rating differs from what was
 *   just clicked) is shown via the "Click again to remove your rating"
 *   tooltip's neighboring status text, not as an error — see the spec's
 *   Sequence B.
 * - Disconnect race: if no `feedbackAck` arrives within `ACK_TIMEOUT_MS` of
 *   a click, this component reverts its own optimistic lock back to
 *   whatever that specific `pending` variant's own failure rule says
 *   (unset for a fresh rate; `prior` for a deselect or a switch's first
 *   half) — a lost frame must not leave the UI stuck at any `pending`
 *   variant forever with no corresponding server record.
 */
import React, { useState, useRef, useEffect } from 'react';
import type { ClientFrame } from '@amzn/soda-brain-protocol';
import { MAX_FEEDBACK_COMMENT_LENGTH } from '@amzn/soda-brain-protocol';
import type { Message, MessageFeedback } from './useConversationSessions';

/** Bounded wait for a feedbackAck after a click, before reverting the optimistic lock. */
const ACK_TIMEOUT_MS = 8_000;

export interface TurnFeedbackProps {
  turnId: string | undefined;
  feedback: Message['feedback'];
  sendFrame: (frame: ClientFrame) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  colors: {
    text: string;
    textMuted: string;
    border: string;
    sidebarBg: string;
  };
}

/**
 * `'closed'` — no popover. `'commenting-fresh'` — collecting a comment for
 * a fresh downvote on an unset turn. `'commenting-switch'` — collecting a
 * comment for a down-switch (decision 14): distinguished from the fresh
 * case because Submit/Skip here must send `deselect` first (with the
 * comment riding in the `switch` state), not `feedback` directly.
 */
type PopoverStep = 'closed' | 'commenting-fresh' | 'commenting-switch';

/**
 * Renders thumbs up/down for a single completed turn, or nothing at all if
 * `turnId` is absent.
 */
export function TurnFeedback({ turnId, feedback, sendFrame, setMessages, colors }: TurnFeedbackProps): React.ReactElement | null {
  const [popoverStep, setPopoverStep] = useState<PopoverStep>('closed');
  const [comment, setComment] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);
  const ackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close the comment popover on outside click, mirroring FeedbackButton's pattern.
  useEffect(() => {
    if (popoverStep === 'closed') return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverStep('closed');
        setComment('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverStep]);

  // Clear any in-flight ack-timeout watchdog on unmount (conversation switch),
  // so a stale timer from a previous mount can't fire after this component
  // is gone — the parent Message state itself is unaffected either way.
  useEffect(() => {
    return () => {
      if (ackTimeoutRef.current) {
        clearTimeout(ackTimeoutRef.current);
      }
    };
  }, []);

  if (!turnId) {
    return null;
  }

  const isPending = feedback !== undefined && 'pending' in feedback;
  const lockedRating = feedback !== undefined && 'rating' in feedback ? feedback.rating : undefined;

  /**
   * Arms (or re-arms) the disconnect-race watchdog for the given optimistic
   * `pending` state, reverting to `revertTo` if no ack arrives in time. A
   * lost frame must not leave the UI stuck at `pending` forever with no
   * corresponding server record.
   *
   * `expectedPending` is the specific `pending` variant this watchdog is
   * guarding (e.g. `'switch'` for `sendSwitch`'s call) — the revert only
   * fires if `feedback.pending` still equals that exact value when the
   * timer expires, not merely "some pending variant or other." This
   * matters specifically for a switch: `handleFrame` transitions
   * `pending: 'switch'` to `pending: 'rate'` the moment the first frame's
   * ack arrives (decision 12) and arms its OWN hook-level watchdog for
   * that second half (`SWITCH_SECOND_FRAME_ACK_TIMEOUT_MS` in
   * `useConversationSessions.ts`) — but this component's watchdog, armed
   * back at the original click, is still live and was previously matching
   * on the looser `'pending' in feedback` check, so it would fire first
   * (it was armed earlier) and revert to the switch's `prior` rating even
   * though the hook's own watchdog exists specifically to revert that
   * later stage to `undefined` instead (a switch's second-frame failure
   * must not resurrect a rating the server has already deleted). Checking
   * the specific variant closes that race: once the hook has moved the
   * state to `pending: 'rate'`, this watchdog's own `'switch'` check no
   * longer matches, and it correctly no-ops, leaving the hook's own
   * watchdog as the sole owner of what happens next.
   */
  function armAckWatchdog(capturedTurnId: string, expectedPending: 'rate' | 'deselect' | 'switch', revertTo: MessageFeedback | undefined): void {
    if (ackTimeoutRef.current) {
      clearTimeout(ackTimeoutRef.current);
    }
    ackTimeoutRef.current = setTimeout(() => {
      setMessages((prev) =>
        prev.map((m) => (m.turnId === capturedTurnId && m.feedback && 'pending' in m.feedback && m.feedback.pending === expectedPending ? { ...m, feedback: revertTo } : m)),
      );
    }, ACK_TIMEOUT_MS);
  }

  /** Fresh rating on a currently-unset turn — the only case that sends `feedback` directly, with no deselect first. */
  function sendFreshRating(rating: 'up' | 'down', trimmedComment?: string): void {
    const capturedTurnId = turnId as string;
    sendFrame({
      action: 'feedback',
      turnId: capturedTurnId,
      rating,
      ...(trimmedComment ? { comment: trimmedComment } : {}),
    });
    setMessages((prev) => prev.map((m) => (m.turnId === capturedTurnId ? { ...m, feedback: { pending: 'rate', target: rating } } : m)));
    armAckWatchdog(capturedTurnId, 'rate', undefined);
  }

  /** Same-button click on a locked rating — deselect, with no comment involved. */
  function sendDeselect(prior: 'up' | 'down'): void {
    const capturedTurnId = turnId as string;
    sendFrame({ action: 'deselect', turnId: capturedTurnId });
    setMessages((prev) => prev.map((m) => (m.turnId === capturedTurnId ? { ...m, feedback: { pending: 'deselect', prior } } : m)));
    armAckWatchdog(capturedTurnId, 'deselect', { rating: prior });
  }

  /**
   * Opposite-button click on a locked rating — switch. Sends `deselect` as
   * the first frame (decision 11); `handleFrame` sends the second
   * `feedback` frame itself once that acks `'removed'` (decision 12). The
   * optional `comment` was already collected before this function is ever
   * called, for a down-switch (decision 14) — see `handleDownClick`.
   */
  function sendSwitch(prior: 'up' | 'down', target: 'up' | 'down', trimmedComment?: string): void {
    const capturedTurnId = turnId as string;
    sendFrame({ action: 'deselect', turnId: capturedTurnId });
    setMessages((prev) =>
      prev.map((m) => (m.turnId === capturedTurnId ? { ...m, feedback: { pending: 'switch', prior, target, comment: trimmedComment } } : m)),
    );
    armAckWatchdog(capturedTurnId, 'switch', { rating: prior });
  }

  function handleUpClick(): void {
    if (feedback === undefined) {
      sendFreshRating('up');
      return;
    }
    if (isPending) return; // decision 12/M1: a click during any in-flight operation is a no-op
    if (lockedRating === 'up') {
      sendDeselect('up');
    } else if (lockedRating === 'down') {
      sendSwitch('down', 'up'); // switching TO up never needs a comment
    }
  }

  function handleDownClick(): void {
    if (feedback === undefined) {
      setPopoverStep('commenting-fresh');
      return;
    }
    if (isPending) return;
    if (lockedRating === 'down') {
      sendDeselect('down');
    } else if (lockedRating === 'up') {
      // decision 14: collect the comment BEFORE sending anything — feedback
      // stays { rating: 'up' } (not yet pending) while this popover is open.
      setPopoverStep('commenting-switch');
    }
  }

  function handleSubmitComment(): void {
    const trimmed = comment.trim();
    const trimmedComment = trimmed.length > 0 ? trimmed : undefined;
    if (popoverStep === 'commenting-switch') {
      sendSwitch('up', 'down', trimmedComment);
    } else {
      sendFreshRating('down', trimmedComment);
    }
    setPopoverStep('closed');
    setComment('');
  }

  function handleSkipComment(): void {
    if (popoverStep === 'commenting-switch') {
      sendSwitch('up', 'down');
    } else {
      sendFreshRating('down');
    }
    setPopoverStep('closed');
    setComment('');
  }

  const buttonBaseStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '14px',
    padding: '2px 6px',
    borderRadius: '4px',
    cursor: isPending ? 'default' : 'pointer',
  };

  function buttonStyle(active: boolean): React.CSSProperties {
    return {
      ...buttonBaseStyle,
      opacity: isPending ? 0.5 : 1,
      background: active ? colors.sidebarBg : 'none',
    };
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '2px', marginTop: '8px' }}>
      <button
        onClick={handleUpClick}
        disabled={isPending}
        title={lockedRating === 'up' ? 'Click again to remove your rating' : 'Good answer'}
        style={buttonStyle(lockedRating === 'up')}
      >
        {feedback && 'pending' in feedback && feedback.pending !== 'deselect' && feedback.target === 'up' ? '⏳' : '👍'}
      </button>
      <button
        onClick={handleDownClick}
        disabled={isPending}
        title={lockedRating === 'down' ? 'Click again to remove your rating' : 'Something was wrong'}
        style={buttonStyle(lockedRating === 'down')}
      >
        {feedback && 'pending' in feedback && feedback.pending !== 'deselect' && feedback.target === 'down' ? '⏳' : '👎'}
      </button>
      {feedback !== undefined && (
        // A duplicate-race convergence (spec Sequence B) can leave the
        // acked rating different from whatever this browser just clicked —
        // this status text is why that's not surfaced as an error.
        <span style={{ fontSize: '11px', color: colors.textMuted, marginLeft: '2px' }} title="Feedback recorded for this answer">
          {isPending ? 'Submitting…' : 'Thanks for the feedback'}
        </span>
      )}

      {(popoverStep === 'commenting-fresh' || popoverStep === 'commenting-switch') && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: '8px',
            backgroundColor: colors.sidebarBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1100,
            width: '260px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: colors.text }}>
            What was incorrect with the response? (optional)
          </div>
          <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '8px' }}>
            Comments are stored to help improve MrPibb.
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={MAX_FEEDBACK_COMMENT_LENGTH}
            placeholder="Describe what was wrong..."
            style={{
              width: '100%',
              minHeight: '60px',
              padding: '8px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              fontSize: '12px',
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: 'transparent',
              color: colors.text,
            }}
            autoFocus
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={handleSkipComment}
              style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '12px' }}
            >
              Skip
            </button>
            <button
              onClick={handleSubmitComment}
              style={{ padding: '6px 12px', backgroundColor: '#7c4dff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
